import { useTranslation } from 'next-i18next';
import { Button, Tooltip } from '@components/common';
import { CheckIcon, BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import {
  BookmarkIcon, ShareIcon,
} from '@heroicons/react/24/outline';

import { ApiJobDetailOk, ApiJobUserDetailOk } from '@type/v1-api-types';
import { useJobTalentActions } from '@hooks/features/useJobTalentActions';
import {
  useContext, useEffect, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { JobTrackingContext } from '@components/contexts/JobTrackingContext';
import { JobPlacement } from '@type/job-params';
import { clsx } from 'clsx';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useBjModal } from '@hooks/common/useBjModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import {
  InterviewResponseButton,
} from '@components/templates/job/after-apply/interview-selection/InterviewResponseButton';
import { PhoneApplyButton } from '@components/features/job-detail/PhoneApply';

// Interface
// ************************************
export interface JobActionsProps {
  job: ApiJobDetailOk & ApiJobUserDetailOk & ApiSchema<'JobItem'>,
  mutateJob?: () => void;
  isModal?: boolean;
  onJobApplied?: (jobSlug: string) => void;
}

/**
 * @description Job detail - Job Actions component.
 * - **job** - ApiJobDetailOk. The job detail.
 */
export const JobActions = (props: JobActionsProps) => {
  // Destructure props
  const {
    job, mutateJob, isModal = false, onJobApplied,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Check whether the user is authenticated
  // **************************************
  const { isSafeAuth } = useAuthClient();

  // Router for locale
  const { locale } = useExtendedRouter();

  // Get tracking context data
  // **************************************
  const {
    requestId,
  } = useContext(JobTrackingContext) || {};

  // Job Talent Actions Hook
  // *****************************************
  const {
    applyToJob,
    isApplying,
    hasApplied,
    isFavorite,
    isLoadingFavoritesAction,
    removeFromFavorites,
    addToFavorites,
  } = useJobTalentActions({
    jobId: job.jobId?.toString(),
    jobTitle: job.title,
    company: job.employer?.employerName,
    jobSlug: job.slug,
    likedProp: !!job.liked,
    dislikedProp: !!job.disliked,
    appliedProp: !!job.applied,
    applyCallback: () => {
      onJobApplied?.(job.slug);
      if (mutateJob) { void mutateJob(); }
    },
    requestId,
    placement: JobPlacement.DETAIL,
    hasOwnApplyUrl: job.hasOwnApplyUrl,
    allowAfterApply: !job?.workplaceJob,
  });

  // Share modal
  const { isOpen: isOpenShareModal, toggleModal: toggleShareModal } = useBjModal();

  // Track mount state to safely use createPortal (avoid SSR mismatch)
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [modalPortalTarget, setModalPortalTarget] = useState<Element | null>(null);
  useEffect(() => {
    setMounted(true);
    if (isModal) {
      const modalPanels = Array.from(document.querySelectorAll('[role="dialog"] [data-modal-scroll-container]'));
      const dialogs = Array.from(document.querySelectorAll('[role="dialog"]'));
      setModalPortalTarget(modalPanels[modalPanels.length - 1] ?? dialogs[dialogs.length - 1] ?? null);
    }
    const mq = window.matchMedia('(max-width: 1023px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [isModal]);

  // Helper Object that stores the classes needed for tracking
  // *****************************************
  const trackingClasses = {
    like: 'tr-job-like',
  };

  const [trackingClassesApply, setTrackingClassesApply] = useState<string>('');

  // Update trackingClassesApply if the user is logged in
  // *****************************************
  useEffect(() => {
    setTrackingClassesApply(clsx('tm-job-apply', !isSafeAuth && 'tr_easy_apply_login'));
  }, [isSafeAuth]);

  // Helper Classes
  // *****************************************
  const buttonBaseClasses = 'px-1 !transition !duration-200 lg:!px-3';
  const buttonLikeClasses = clsx(buttonBaseClasses, trackingClasses.like);
  const buttonIconClasses = 'size-5';
  const buttonSpinnerClasses = 'size-5 mx-0 text-white';

  // Whether the candidate has applied but has unanswered interview questions
  const needToCompleteInterview = !!(job?.appliedAt && job?.unansweredJobInterviewQuestions && job?.unansweredJobInterviewQuestions?.length > 0);

  // Render component
  // *****************************************
  const renderApplicationActions = () => {
    // Return nothing if job.state is missing or falsy
    if (!job?.state) {
      return null;
    }

    if (job?.state === 'archived' || hasApplied) {
      return null;
    }

    return (
      <>
        {/* Apply button */}
        <Button
          type="button"
          size="sm"
          className={clsx('flex-1 transition! duration-200!', trackingClassesApply)}
          color="secondary"
          rounding="2xl"
          isLoading={isApplying}
          onClick={applyToJob}
        >
          {!isApplying && <CheckIcon className="-ml-1 mr-2 size-4" />}
          <span>
            {(() => {
              if (job?.state === 'active') {
                return t('job.apply.start.interview');
              }
              if (job?.state === 'passive') {
                return t('global.bnt.fast.apply.workplace.job');
              }
              return null;
            })()}
          </span>
        </Button>
      </>
    );
  };

  const actionBar = (
    <div
      className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-50 mx-4 flex items-center justify-center gap-2 rounded-3xl border border-input bg-surface px-4 py-5 shadow-lg backdrop-blur-sm lg:sticky lg:inset-x-auto lg:bottom-4 lg:mx-8 lg:gap-5 lg:rounded-3xl lg:px-7"
    >

      {/* Share button */}
      {job?.slug && job?.title && (
      <Tooltip
        tooltipText={t('jobs-share')}
        position="top"
        colorScheme="black"
        floating
        useFloatingPortal
        className="relative inline-flex"
        triggerContainerClass="inline-flex"
      >
        <Button
          type="button"
          rounding="2xl"
          color="light"
          size="sm"
          className="p-3!"
          onClick={toggleShareModal}
        >
          <ShareIcon className="size-5" />
        </Button>
      </Tooltip>
      )}

      {/* Save / Favorite button */}
      {job?.state && job?.state !== 'archived' && !hasApplied && (
        <Tooltip
          tooltipText={t('jobs-save-job')}
          position="top"
          colorScheme="black"
          floating
          useFloatingPortal
          className="relative inline-flex"
          triggerContainerClass="inline-flex"
        >
          <Button
            type="button"
            rounding="2xl"
            size="sm"
            color="light"
            isLoading={isLoadingFavoritesAction}
            className={clsx(buttonLikeClasses, 'p-2.5! lg:px-3!')}
            spinnerClass={buttonSpinnerClasses}
            onClick={isFavorite ? removeFromFavorites : addToFavorites}
          >
            {!isLoadingFavoritesAction && (
              isFavorite
                ? <BookmarkIconSolid className={buttonIconClasses} />
                : <BookmarkIcon className={buttonIconClasses} />
            )}
          </Button>
        </Tooltip>
      )}

      {renderApplicationActions()}

      {/* Guest-only: apply-by-phone shortcut. Renders next to the apply
          button on the public job detail page. Mounted via the
          isSafeAuth guard + the same active/passive-state gate as the
          apply button itself — same conditions where applying makes
          sense. See docs/plans/phone-apply-flow.md. */}
      {!isSafeAuth && !hasApplied && job?.slug && (job.state === 'active' || job.state === 'passive') && (
        <PhoneApplyButton locale={locale} jobSlug={job.slug} />
      )}

      {/* Start Interview button - only if applied and has unanswered interview questions */}
      {needToCompleteInterview && (
        <div className="my-1 flex flex-col items-start gap-2">
          {needToCompleteInterview && (
            <InterviewResponseButton
              jobSlug={job.slug}
              className="flex flex-1"
            />
          )}
        </div>
      )}

    </div>
  );

  return (
    <>
      {/* On mobile, portal the action bar outside the swipe container so `position: fixed`
          is not captured by the translateX transform. In modals, keep it inside the
          dialog panel so Headless UI treats button clicks as modal content clicks. */}
      {mounted && isMobile ? createPortal(actionBar, isModal && modalPortalTarget ? modalPortalTarget : document.body) : actionBar}

      {/* Share modal */}
      {job?.slug && job?.title && (
        <ShareModal
          isOpen={isOpenShareModal}
          onClose={toggleShareModal}
          ogTitle={job.title}
          ogDescription={job.title}
          pathnameShareUrl={getI18nLinkHref(`/job/${job.slug}`, locale)}
        />
      )}
    </>
  );
};
